import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toDate } from '@/lib/date-display.js';

const LOGO_URL = 'https://ndemnifly.com/assets/Ndemnifly-MainLogo-Teal-scaled.png';

// Decision 16: an amount nobody stated reads "Not stated" in grey, never $0.00.
function statedAmount(v) {
  if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmtCurrency(amount) {
  const n = statedAmount(amount);
  return n == null ? null : '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function Money({ v }) {
  const s = fmtCurrency(v);
  return s == null ? <span data-not-stated className="text-gray-400 font-normal">Not stated</span> : s;
}

function fmtDate(dateStr) {
  if (!dateStr) return '';
  const dt = toDate(dateStr);
  if (isNaN(dt)) return dateStr;
  return dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export default function PaymentRequest() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showNickel, setShowNickel] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const res = await fetch(`/api/session/${token}/payment-request?_t=${Date.now()}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Error ${res.status}`);
      }
      const d = await res.json();
      setData(d);
      if (d.payment_request?.acknowledgment) {
        setAcknowledged(true);
        setChecked(true);
        setShowNickel(true);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handlePay() {
    if (!checked || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`/api/session/${token}/acknowledge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Acknowledge failed');
      }
      setAcknowledged(true);
      setShowNickel(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <img src={LOGO_URL} alt="NDEMNIFLY" className="h-8 mx-auto mb-6" />
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const pr = data.payment_request || {};
  const breakdown = pr.amount_breakdown || {};
  const period = pr.policy_period || {};
  const session = data.session || {};
  const payment = data.payment || {};
  const held = data.payment_held || null;

  const isCancelled = session.status === 'lost';
  const isBound = session.status === 'bound';

  if (isCancelled) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <img src={LOGO_URL} alt="NDEMNIFLY" className="h-8 mx-auto mb-6" />
          <h1 className="text-lg font-semibold text-gray-900 mb-2">Payment link cancelled</h1>
          <p className="text-sm text-gray-500">This payment request has been cancelled by the broker. If you believe this is an error, please contact NDEMNIFLY Aviation Insurance.</p>
        </div>
      </div>
    );
  }

  if (isBound) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <img src={LOGO_URL} alt="NDEMNIFLY" className="h-8 mx-auto mb-6" />
          <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold text-gray-900 mb-2">Payment received</h1>
          <p className="text-sm text-gray-500">Your payment has been confirmed and your coverage is in effect. You will receive your policy documents by email shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-2xl mx-auto px-4 py-5">
          <img src={LOGO_URL} alt="NDEMNIFLY Aviation Insurance" className="h-8" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Recap card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="mb-4">
            <h1 className="text-xl font-semibold text-gray-900">{pr.carrier_name}</h1>
            <p className="text-sm text-gray-500 mt-1">
              Effective {fmtDate(period.effective)}, expires {fmtDate(period.expiration)}
            </p>
            {pr.policy_number && (
              <p className="text-sm text-gray-500">Policy {pr.policy_number}</p>
            )}
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-3">{pr.what_youre_paying_for}</p>
          {pr.broker_message && (
            <p className="text-gray-500 text-sm leading-relaxed italic">{pr.broker_message}</p>
          )}
        </div>

        {/* Amount breakdown */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Amount due</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Premium</span>
              <span className="text-gray-900 font-medium"><Money v={breakdown.premium} /></span>
            </div>
            {(breakdown.estimated_taxes > 0) && (
              <div className="flex justify-between">
                <span className="text-gray-600">Estimated taxes & fees</span>
                <span className="text-gray-900 font-medium"><Money v={breakdown.estimated_taxes} /></span>
              </div>
            )}
            {/* The carrier's program fee, its own line (carrier-fee-01, David 2026-10-31). */}
            {(Number(breakdown.carrier_fee) > 0) && (
              <div className="flex justify-between">
                <span className="text-gray-600">Carrier program fee</span>
                <span className="text-gray-900 font-medium"><Money v={breakdown.carrier_fee} /></span>
              </div>
            )}
            {/* Deferred items riding this renewal (184 §4). ONE PAYMENT, TWO
                OBLIGATIONS, NEVER ONE NUMBER: each is its own named, dated
                line so the client can tell what they are paying for without
                calling to ask, and the premium above stays the premium.
                COPY IS PLACEHOLDER until David reviews it. */}
            {(breakdown.deferred_lines || []).map((line) => (
              <div key={line.transaction_id} className="flex justify-between gap-4">
                <span className="text-gray-600">{line.label}</span>
                <span className="text-gray-900 font-medium whitespace-nowrap"><Money v={line.amount} /></span>
              </div>
            ))}
            <div className="flex justify-between">
              {/* The deal's fee as the server words it (utils/platform-fee): WAIVED in place of $0.00. */}
              <span className="text-gray-600">{breakdown.platform_fee_line?.label || 'Platform fee'}</span>
              <span className="text-gray-900 font-medium">{breakdown.platform_fee_line?.display || <Money v={breakdown.platform_fee} />}</span>
            </div>
            {/* The agency fee, only when the deal has one: no line, never $0. */}
            {breakdown.agency_fee_line ? (
              <div className="flex justify-between">
                <span className="text-gray-600">{breakdown.agency_fee_line.label}</span>
                <span className="text-gray-900 font-medium"><Money v={breakdown.agency_fee_line.amount} /></span>
              </div>
            ) : null}
            <div className="border-t border-gray-200 pt-3 flex justify-between">
              <span className="text-gray-900 font-semibold">Total due</span>
              <span className="text-gray-900 font-semibold text-lg"><Money v={breakdown.total} /></span>
            </div>
          </div>
        </div>

        {/* The server holds this payment (payment_held: nothing payable is
            stated, B5). Its sentence as given, no box, no Pay button, no
            Nickel form (W6 part 3; public/pay.html does the same). */}
        {held && (
          <div data-held className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <p className="text-sm text-gray-700 leading-relaxed">{held.message}</p>
          </div>
        )}

        {/* Acknowledgment + Pay */}
        {!held && !showNickel && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <label className="flex items-start gap-3 cursor-pointer mb-5">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-teal-700 focus:ring-teal-600"
              />
              <span className="text-sm text-gray-700 leading-relaxed">
                {pr.acknowledgment_text_display}
              </span>
            </label>

            <button
              onClick={handlePay}
              disabled={!checked || submitting}
              className="w-full bg-[#094356] text-white py-3 px-6 rounded-lg font-medium text-base hover:bg-[#0b536a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Processing...' : `Pay ${fmtCurrency(breakdown.total)}`}
            </button>
          </div>
        )}

        {/* Nickel iframe */}
        {!held && showNickel && payment.payUrl && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
            <iframe
              src={payment.payUrl}
              title="Payment"
              width="100%"
              height="600"
              style={{ border: 'none' }}
              allow="payment"
            />
          </div>
        )}

        {/* Footer */}
        <footer className="text-center text-xs text-gray-400 py-6">
          <p>NDEMNIFLY Aviation Insurance &bull; Athens, GA</p>
          <p className="mt-1">
            Questions? Call <a href="tel:+17062050200" className="text-teal-700 hover:underline">706-205-0200</a>
          </p>
        </footer>
      </main>
    </div>
  );
}
